import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const About = () => {
  useScrollToTop();
  const statsAnim = useScrollAnimation();
  const missionAnim = useScrollAnimation();
  const valuesAnim = useScrollAnimation();
  const teamAnim = useScrollAnimation();
  const storyAnim = useScrollAnimation();
  const ctaAnim = useScrollAnimation();

  const stats = [
    { number: '500+', label: 'Startups Launched' },
    { number: '150+', label: 'Expert Mentors' },
    { number: '95%', label: 'Success Rate' },
    { number: '$50M+', label: 'Funding Raised' }
  ];

  const team = [
    { name: 'Sarah Johnson', role: 'CEO & Co-Founder', bio: 'Former VP at TechCorp, 15+ years in startup ecosystem', image: 'bg-chart/20' },
    { name: 'Michael Chen', role: 'CTO & Co-Founder', bio: 'Ex-Google engineer, serial entrepreneur with 3 successful exits', image: 'bg-chart/20' },
    { name: 'Emily Rodriguez', role: 'Head of Mentorship', bio: 'Former Y Combinator partner, helped 200+ startups scale', image: 'bg-chart/20' },
    { name: 'David Kim', role: 'Head of Programs', bio: 'Former McKinsey consultant, startup advisor for 10+ years', image: 'bg-chart/20' }
  ];

  const values = [
    {
      icon: (<svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>),
      title: 'Excellence',
      description: 'We maintain the highest standards in everything we do, from mentor selection to program delivery.'
    },
    {
      icon: (<svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" /></svg>),
      title: 'Community',
      description: 'We believe in the power of group mentorship and peer learning to accelerate entrepreneurial success.'
    },
    {
      icon: (<svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" /></svg>),
      title: 'Innovation',
      description: 'We constantly evolve our programs and platform to stay at the forefront of startup education.'
    },
    {
      icon: (<svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" /></svg>),
      title: 'Transparency',
      description: 'We provide clear metrics, honest feedback, and transparent processes throughout the journey.'
    }
  ];

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-bg text-text">
      <Header />

      <div className="section-gap pt-20 pb-12">

        {/* Hero */}
        <section className="text-center py-20 animate-fade-in">
          <h1 className="font-display text-[4.5rem] md:text-[6rem] leading-none text-text uppercase tracking-wide mb-8">
            Empowering the Next Generation of Entrepreneurs
          </h1>
          <p className="font-serif-display text-2xl md:text-3xl italic text-muted max-w-4xl mx-auto mb-12 leading-relaxed">
            BunyadKar is a virtual incubation model that takes founders through a structured 6-phase journey —
            from coursework and group mentorship to pitching and investor review — all online.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/register">
              <Button variant="primary" size="lg" className="px-8 py-4 text-lg">Join Our Community</Button>
            </Link>
            <Link to="/contact">
              <Button variant="ghost" size="lg" className="px-8 py-4 text-lg">Get in Touch</Button>
            </Link>
          </div>
        </section>

        {/* Stats */}
        <section ref={statsAnim.ref} className={`py-16 border-t border-line animate-on-scroll ${statsAnim.isVisible ? 'visible' : ''}`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="font-display text-6xl text-chart leading-none mb-3">{stat.number}</div>
                <div className="font-serif-display text-xl italic text-muted">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Mission & Vision */}
        <section ref={missionAnim.ref} className={`py-16 animate-on-scroll ${missionAnim.isVisible ? 'visible' : ''}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="font-display text-[3.5rem] leading-none text-text uppercase tracking-wide mb-6">Our Mission</h2>
              <p className="font-serif-display text-2xl italic text-muted mb-6 leading-relaxed">
                To democratize entrepreneurship by providing world-class education, group mentorship, and resources
                to aspiring founders regardless of their background or location through our virtual incubation model.
              </p>
              <p className="font-serif-display text-2xl italic text-muted leading-relaxed">
                We believe that great ideas can come from anywhere, and with the right support,
                anyone can build a successful startup that makes a positive impact on the world.
              </p>
            </div>
            <div>
              <h2 className="font-display text-[3.5rem] leading-none text-text uppercase tracking-wide mb-6">Our Vision</h2>
              <p className="font-serif-display text-2xl italic text-muted mb-6 leading-relaxed">
                To become the leading virtual incubation platform that transforms how entrepreneurs
                learn, connect, and build their startups.
              </p>
              <p className="font-serif-display text-2xl italic text-muted leading-relaxed">
                We envision a future where every aspiring entrepreneur has access to the tools,
                knowledge, and network they need to turn their ideas into reality.
              </p>
            </div>
          </div>
        </section>

        {/* Values */}
        <section ref={valuesAnim.ref} className={`py-16 border-t border-line animate-on-scroll ${valuesAnim.isVisible ? 'visible' : ''}`}>
          <h2 className="font-display text-[3.5rem] leading-none text-text uppercase tracking-wide mb-12 text-center">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {values.map((value, index) => (
              <div key={index} className="flex items-start space-x-6">
                <div className="flex-shrink-0 w-16 h-16 bg-accent-soft rounded-xl flex items-center justify-center text-chart">
                  {value.icon}
                </div>
                <div>
                  <h3 className="font-display text-3xl tracking-wide text-text mb-3">{value.title}</h3>
                  <p className="font-serif-display text-xl italic text-muted leading-relaxed">{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Team */}
        <section ref={teamAnim.ref} className={`py-16 animate-on-scroll ${teamAnim.isVisible ? 'visible' : ''}`}>
          <h2 className="font-display text-[3.5rem] leading-none text-text uppercase tracking-wide mb-12 text-center">Meet Our Team</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div key={index} className="text-center">
                <div className={`w-24 h-24 ${member.image} rounded-full mx-auto mb-6`} />
                <h3 className="font-display text-2xl tracking-wide text-text mb-1">{member.name}</h3>
                <p className="text-chart font-serif-display text-lg italic mb-3">{member.role}</p>
                <p className="font-serif-display text-lg italic text-muted leading-relaxed">{member.bio}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Story */}
        <section ref={storyAnim.ref} className={`py-16 border-t border-line animate-on-scroll ${storyAnim.isVisible ? 'visible' : ''}`}>
          <div className="text-center">
            <h2 className="font-display text-[3.5rem] leading-none text-text uppercase tracking-wide mb-8">Our Story</h2>
            <div className="max-w-5xl mx-auto space-y-6">
              <p className="font-serif-display text-2xl italic text-muted leading-relaxed">
                BunyadKar was born from a simple observation: too many brilliant ideas never see the light of day
                because their creators lack access to the right resources, mentorship, and community.
              </p>
              <p className="font-serif-display text-2xl italic text-muted leading-relaxed">
                Founded by a team of serial entrepreneurs and startup veterans, we set out to create a
                platform that would level the playing field and make world-class startup education accessible to everyone.
              </p>
              <p className="font-serif-display text-2xl italic text-muted leading-relaxed">
                Today, we're proud to have helped hundreds of entrepreneurs launch their startups, raise funding,
                and build successful businesses that are making a real difference in the world.
              </p>
            </div>
          </div>
        </section>

        {/* Mentor CTA */}
        <section className="py-16 border-t border-line">
          <div className="bg-accent-soft rounded-card p-8 md:p-12 text-center">
            <h2 className="font-display text-[3rem] leading-none text-text uppercase tracking-wide mb-4">Interested in Mentoring?</h2>
            <p className="font-serif-display text-2xl italic text-muted mb-8 max-w-2xl mx-auto">
              We are always looking for experienced professionals to lead group mentorship sessions.
              Share your expertise and help shape the next generation of founders.
            </p>
            <Link to="/contact">
              <Button variant="primary" size="lg" className="px-8 py-4 text-lg">Contact Us About Mentoring</Button>
            </Link>
          </div>
        </section>

        {/* CTA */}
        <section ref={ctaAnim.ref} className={`py-20 text-center animate-on-scroll ${ctaAnim.isVisible ? 'visible' : ''}`}>
          <h2 className="font-display text-[3.5rem] leading-none text-text uppercase tracking-wide mb-6">Ready to Start Your Journey?</h2>
          <p className="font-serif-display text-2xl italic text-muted mb-10 max-w-2xl mx-auto">
            Join thousands of entrepreneurs who are building the future with BunyadKar.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/register">
              <Button variant="primary" size="lg" className="px-8 py-4 text-lg">Get Started Today</Button>
            </Link>
            <Link to="/contact">
              <Button variant="ghost" size="lg" className="px-8 py-4 text-lg">Contact Us</Button>
            </Link>
          </div>
        </section>

      </div>

      <Footer />
    </div>
  );
};

export default About;
